import { PERMISSIONS } from 'api/User/types'
import classNames from 'classnames'
import CommonIcon from 'CommonIcon'
import { InfoElement } from 'components'
import { InfoArrow } from 'components/InfoArrow'
import { CurrencyValue } from 'CurrencyValue'
import type { DetailsData } from 'data/types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import RestrictedView from 'RestrictedView'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import useStyles from './useStyles'
import { getCurrencyValue, getPercent } from './utils'

interface Props {
  data: DetailsData
}

function DetailsDefault({ data }: Props) {
  const { t: translate } = useTranslation()
  const classes = useStyles()

  return (
    <Grid container item xs={9} justifyContent="space-between">
      <Grid container item xs={12} className={classes.row1} mt="0.7rem">
        <Grid container item xs={5} alignItems="baseline" display="row">
          <CurrencyValue baseFontSize={1.75} currency={data.currency} value={data.revenueCurrent} />
          <InfoElement className={classes.percent} percent={data.revenuePercent} />
        </Grid>
        <Grid container item xs={7} display="row">
          <Box className={`fm fm-l4l ${classes.label}`} alignSelf="baseline" />
          {getCurrencyValue(data.revenueCurrentL4L, data.currency, translate)}
          <InfoElement className={classes.percent} percent={data.revenuePercentL4L} />
          <div className={classes.separator} />
          {getCurrencyValue(data.revenuePerStoreCurrentL4L, data.currency, translate)}
        </Grid>
      </Grid>

      <Grid container item xs={5} alignItems="baseline" display="row" mt="0.25rem">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <i className={`fm fm-margin ${classes.label}`} />
          <div className={classes.marginPercent}>{getPercent(data.marginCurrent, translate)}</div>
          <div>
            <div className={classes.marginValue}>{getCurrencyValue(data.marginValueCurrent, data.currency, translate)}</div>
            <InfoArrow current={data.marginValueCurrent} compared={data.marginValueCompared} />
          </div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row" mt="0.25rem">
        <i className={classNames('fm fm-at', classes.label)} />
        <div className={classes.ecomShare}>
          <div className={classes.ecom}>{getPercent(data.ecomShareCurrent, translate)}</div>
        </div>
        <Box alignItems="baseline" className={classes.marginComparedValue} display="flex">
          {getCurrencyValue(data.revenueCurrentECom, data.currency, translate)}
          <InfoElement className={classes.percent} percent={data.revenuePercentECom} />
        </Box>
      </Grid>

      <Grid container item xs={5} display="row">
        <RestrictedView permission={PERMISSIONS.MARGIN}>
          <CommonIcon className={classNames(classes.label, classes.lastYear)} name="lastYear" />
          <div className={classes.marginPercent}>{getPercent(data.marginCompared, translate)}</div>
          <div className={classes.marginComparedValue}>{getCurrencyValue(data.marginValueCompared, data.currency, translate)}</div>
        </RestrictedView>
      </Grid>
      <Grid container item xs={7} alignItems="baseline" display="row">
        <div className={classNames(classes.label, classes.avg)}>m²</div>
        {getCurrencyValue(data.floorSpaceCurrent, 'm²', translate, 0)}
        <InfoElement className={classes.percent} percent={data.floorSpacePercent} />
      </Grid>

      <Grid container item xs={11} className={classes.row3} alignItems="baseline" justifyContent="space-between">
        <Box>
          <i className={classNames('fm fm-order-checked', classes.label)} />
          {getCurrencyValue(data.ordersValueCurrent, data.currency, translate)}
          <InfoElement className={classes.percent} percent={data.ordersValuePercent} />
        </Box>
        <Box>
          <Box className={`fm fm-oms ${classes.label}`} />
          {getPercent(data.ordersValueOMSShareCurrent, translate)}
          {' | '}
          {getCurrencyValue(data.ordersValueOMSCurrent, data.currency, translate)}
        </Box>
        <Box>
          <i className={`fm fm-instore ${classes.label}`} />
          {getPercent(data.ordersValueInStoreShareCurrent, translate)}
          {' | '}
          {getCurrencyValue(data.ordersValueInStoreCurrent, data.currency, translate)}
        </Box>
      </Grid>
      <Grid item xs={1} className={classes.row3} />
    </Grid>
  )
}

export default DetailsDefault
